import { useCallback, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/lib/realtime";
import useAddons from "@/hooks/useAddons";
import DispatchMap from "@/components/DispatchMap";
import { emergencyAlarm, blip } from "@/lib/audio";
import { Home, Phone, MapPin, BellRing, Menu } from "lucide-react";
import { mt, deviceId, getGps, vibrate } from "@/components/mobile/shared";
import { MobileHome } from "@/components/mobile/MobileHome";
import { MobileCalls } from "@/components/mobile/MobileCalls";
import { MobileAlerts } from "@/components/mobile/MobileAlerts";
import { MobileMore } from "@/components/mobile/MobileMore";
import { MobileEmergency } from "@/components/mobile/MobileEmergency";
import { MobileAddressIntel } from "@/components/mobile/MobileAddressIntel";
import { MobileReports } from "@/components/mobile/MobileReports";

const TABS = [["home", "HOME", Home], ["calls", "CALLS", Phone], ["map", "MAP", MapPin],
  ["alerts", "ALERTS", BellRing], ["more", "MORE", Menu]];
const QUEUE_KEY = "mob_status_queue";

export default function MobileOps() {
  const { user, logout } = useAuth();
  const flags = useAddons();
  const [tab, setTab] = useState("home");
  const [home, setHome] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [units, setUnits] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unacked, setUnacked] = useState(0);
  const [emgOpen, setEmgOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [settings, setSettings] = useState({
    textScale: localStorage.getItem("mob_textsize") || "1",
    contrast: localStorage.getItem("mob_contrast") === "1",
  });
  const alarmedIds = useRef(new Set());
  const myUnit = user?.badge || null;

  const saveSettings = (s) => {
    setSettings(s);
    localStorage.setItem("mob_textsize", s.textScale);
    localStorage.setItem("mob_contrast", s.contrast ? "1" : "0");
  };

  const refresh = useCallback(async () => {
    try {
      const [h, inc, al, un] = await Promise.all([
        client.get("/mobile/home"),
        client.get("/incidents", { params: { active: true, limit: 100 } }),
        client.get("/mobile/alerts", { params: { limit: 100 } }),
        client.get("/units"),
      ]);
      setHome(h.data);
      setIncidents(inc.data || []);
      setUnits(un.data || []);
      setUnacked(al.data.unacked_critical || 0);
      const list = al.data.alerts || [];
      setAlerts(list);
      setLastSync(new Date());
      // Distinct emergency tone + vibration for NEW unacked emergency/critical alerts.
      list.forEach((a) => {
        const acked = (a.acks || []).some((x) => x.user === user.name);
        if (a.require_ack && !a.resolved && !acked && !alarmedIds.current.has(a.id)) {
          alarmedIds.current.add(a.id);
          if (a.severity === "emergency") { emergencyAlarm(); vibrate([300, 100, 300, 100, 300]); }
          else { blip(); vibrate([120, 60, 120]); }
        }
      });
    } catch { /* offline or auth issue — banner covers it */ }
  }, [user?.name]);

  // Device registration + revocation check (§96).
  useEffect(() => {
    client.post("/mobile/device/register", {
      device_id: deviceId(), platform: navigator.platform || "unknown",
      ua: navigator.userAgent.slice(0, 180),
    }).then((r) => {
      if (r.data && r.data.active === false) {
        toast.error("This device has been disabled by an administrator.");
        logout();
      }
    }).catch(() => {});
  }, [logout]);

  // §102 session handoff: restore last view from server, then sync view changes.
  const restoredRef = useRef(false);
  useEffect(() => {
    client.get("/mobile/session-state").then((r) => {
      const s = r.data || {};
      if (!restoredRef.current && s.tab && s.device_id && s.device_id !== deviceId()) {
        setTab(s.tab);
        toast.info(`Resumed from your other device (${s.tab.toUpperCase()} view restored)`);
      }
      restoredRef.current = true;
    }).catch(() => { restoredRef.current = true; });
  }, []);
  useEffect(() => {
    if (!restoredRef.current) return;
    client.post("/mobile/session-state", { tab, device_id: deviceId() }).catch(() => {});
  }, [tab]);

  // Poll + realtime invalidation.
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);
  useRealtime(["incidents", "units", "messages", "mobile_alerts"], refresh);

  // Offline handling + queued status flush (§95).
  useEffect(() => {
    const up = async () => {
      setOnline(true);
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
      if (q.length) {
        for (const item of q) {
          try {
            await client.patch(`/units/${item.unit}/status`, { ...item.body, offline_ts: item.ts });
            await client.post("/mobile/status-audit", { ...item.audit, offline_queued: true });
          } catch { /* keep going; server dedupes via event log */ }
        }
        localStorage.removeItem(QUEUE_KEY);
        toast.success(`Synchronized ${q.length} queued status change(s)`);
        refresh();
      }
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, [refresh]);

  const unitDoc = units.find((u) => u.unit_id === myUnit) || home?.unit;

  // Status change with GPS + audit; queues while offline (never claims success).
  const setStatus = useCallback(async (status, extra = {}, incidentCc = null) => {
    if (!myUnit) { toast.error("No dispatchable unit tied to your account."); return; }
    const gps = await getGps();
    const body = { status, ...extra, ...(gps.lat != null ? gps : {}) };
    const audit = { unit: myUnit, previous: unitDoc?.status || null, requested: status,
      final: status, device: "mobile", device_id: deviceId(),
      incident_cc: incidentCc || unitDoc?.current_incident || null,
      reason: extra.reason || null, ...(gps.lat != null ? gps : {}) };
    if (!navigator.onLine) {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
      q.push({ unit: myUnit, body, audit, ts: new Date().toISOString() });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
      toast.warning("OFFLINE — status change QUEUED, will transmit on reconnect");
      return;
    }
    try {
      await client.patch(`/units/${myUnit}/status`, body);
      client.post("/mobile/status-audit", audit).then((r) => {
        if (r.data?.conflict) {
          toast.warning(`⚠️ Another device set ${r.data.conflict.other_status?.toUpperCase()} for ${myUnit} moments ago — supervisors notified. Verify correct status.`, { duration: 8000 });
        }
      }).catch(() => {});
      blip();
      toast.success(`Status: ${status.replace(/_/g, " ").toUpperCase()}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed"); }
  }, [myUnit, unitDoc, refresh]);

  const requestBackup = useCallback(async (cc) => {
    const gps = await getGps();
    try {
      await client.post("/messages", {
        text: `🚔 BACKUP REQUESTED by ${myUnit || user.name}${cc ? ` on ${cc}` : ""}`,
        scope: "broadcast", channel: "dispatch", from_unit: myUnit,
        incident_cc: cc || null, priority: true, gps: gps.lat != null ? gps : null,
      });
      vibrate();
      toast.success("Backup request transmitted to dispatch");
    } catch { toast.error("Backup request failed — use radio"); }
  }, [myUnit, user?.name]);

  if (!user) return null;
  if (flags && Object.keys(flags).length > 0 && flags.mobile_ops === false) {
    return (
      <div className="h-screen flex items-center justify-center font-mono text-sm p-6 text-center" style={{ background: mt.bg, color: mt.dim }}>
        Mobile Operations Mode is disabled for this agency. An administrator can enable it in the Add-on Manager.
      </div>
    );
  }

  const validUnits = units.filter((u) => u.lat && u.lng);
  const mapIncidents = incidents.filter((i) => i.location?.lat && i.location?.lng);

  return (
    <div data-testid="mobile-ops" className="min-h-screen flex flex-col"
      style={{ background: settings.contrast ? "#000" : mt.bg, fontSize: `${settings.textScale}rem` }}>
      {/* Top bar */}
      <div className="sticky top-0 z-[700] px-3 py-2.5 flex items-center justify-between"
        style={{ background: settings.contrast ? "#000" : "#0a1526", borderBottom: `1px solid ${mt.border}` }}>
        <span className="font-head font-bold text-sm" style={{ color: mt.text }}>MATCOM MOBILE OPS</span>
        <span className="text-[10px] font-mono" style={{ color: online ? mt.green : mt.red }}>
          {online ? "● LIVE" : "● OFFLINE"}
        </span>
      </div>
      {!online && (
        <div data-testid="mobile-offline-banner" className="px-3 py-2 text-center font-head font-bold text-xs"
          style={{ background: mt.red, color: "#fff" }}>
          OFFLINE — drafts &amp; status changes queue locally. Last sync: {lastSync ? lastSync.toLocaleTimeString() : "never"}. Use radio for emergencies.
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {tab === "home" && <MobileHome home={home} user={user} onStatus={setStatus} onTab={setTab} onEmergency={() => setEmgOpen(true)} />}
        {tab === "calls" && <MobileCalls incidents={incidents} myUnit={myUnit} unitDoc={unitDoc} onStatus={setStatus} onBackup={requestBackup} />}
        {tab === "map" && (
          <div data-testid="mobile-map" style={{ height: "calc(100vh - 160px)" }}>
            <DispatchMap units={validUnits} incidents={mapIncidents} height="100%" showDisciplineFilter={false} />
          </div>
        )}
        {tab === "alerts" && <MobileAlerts alerts={alerts} user={user} onRefresh={refresh} />}
        {tab === "more" && <MobileMore user={user} myUnit={myUnit} onSettings={saveSettings} settings={settings} onView={setTab} />}
        {tab === "address" && (
          <MobileAddressIntel user={user} onBack={() => setTab("more")}
            defaultAddress={home?.active_incident?.location?.address || ""} />
        )}
        {tab === "reports" && (
          <MobileReports myUnit={myUnit} onBack={() => setTab("more")}
            activeIncidentCc={unitDoc?.current_incident || home?.active_incident?.cc_number} />
        )}
      </div>

      {/* Persistent emergency button (§87) — never covers nav */}
      <button data-testid="mobile-emergency-fab" onClick={() => setEmgOpen(true)}
        className="fixed right-3 bottom-24 z-[800] w-16 h-16 rounded-full font-head font-bold text-[10px] active:scale-95"
        style={{ background: mt.red, color: "#fff", border: "3px solid #7f1d1d", boxShadow: "0 4px 20px rgba(220,38,38,0.6)" }}>
        🚨<br />EMER
      </button>

      {/* Bottom navigation (§83) */}
      <div data-testid="mobile-bottom-nav" className="fixed bottom-0 left-0 right-0 z-[750] grid grid-cols-5"
        style={{ background: settings.contrast ? "#000" : "#0a1526", borderTop: `1px solid ${mt.border}`, paddingBottom: "env(safe-area-inset-bottom)" }}>
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`mobile-nav-${k}`} onClick={() => setTab(k)}
            className="relative flex flex-col items-center gap-0.5 py-2.5"
            style={{ color: tab === k || (k === "more" && ["address", "reports"].includes(tab)) ? mt.cyan : mt.dim }}>
            <Icon size={20} />
            <span className="text-[9px] font-head font-bold">{label}</span>
            {k === "alerts" && unacked > 0 && (
              <span data-testid="mobile-alert-badge" className="absolute top-1 right-1/4 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                style={{ background: mt.red, color: "#fff" }}>{unacked}</span>
            )}
          </button>
        ))}
      </div>

      <MobileEmergency open={emgOpen} onClose={() => setEmgOpen(false)} unit={myUnit}
        incidentCc={unitDoc?.current_incident} onActivated={refresh} />
    </div>
  );
}
